/**
 * KpiCard — the left-aligned dense stat tile used by the e-commerce and
 * Amazon dashboards. (MetricCard remains the centered variant.)
 * Exports the Delta period-over-period indicator rendered inside it.
 */

import type { ReactNode } from "react";

/**
 * Renders a period-over-period change. Ramp months produce absurd
 * percentages (e.g. +852%), so anything past ±150% is shown as a cleaner
 * multiple ("8.5×") instead. Null/zero prior renders nothing.
 */
export function Delta({
  current,
  prior,
  label,
}: {
  current: number;
  prior: number | null | undefined;
  label: string;
}) {
  if (prior === null || prior === undefined || prior === 0) return null;
  const change = ((current - prior) / Math.abs(prior)) * 100;
  const up = change >= 0;
  const useMultiple = prior > 0 && current > 0 && Math.abs(change) >= 150;
  const text = useMultiple
    ? `${(current / prior).toFixed(1)}×`
    : `${up ? "+" : ""}${change.toFixed(1)}%`;
  return (
    <div
      className={`text-xs font-medium ${up ? "text-emerald-600" : "text-red-600"}`}
    >
      {up ? "▲" : "▼"} {text}{" "}
      <span className="text-gray-400 font-normal">{label}</span>
    </div>
  );
}

export function KpiCard({
  label,
  value,
  valueColor,
  deltas,
  footnote,
}: {
  label: string;
  value: string;
  valueColor?: string;
  deltas?: ReactNode;
  footnote?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <div className="microlabel">{label}</div>
      <div
        className={`mt-1 text-xl font-bold tracking-tight tabular-nums ${valueColor || "text-gray-900"}`}
      >
        {value}
      </div>
      {deltas && <div className="mt-1.5 space-y-0.5">{deltas}</div>}
      {footnote && <div className="mt-1 text-xs text-gray-400">{footnote}</div>}
    </div>
  );
}
